import { atom, useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSnackbar } from '../store/snackbar';
import { OodThemeMode, useThemeMode } from '../themes/themeModeProvider';
import dom2image from 'dom-to-image';
import jsPDF from 'jspdf';

const GUTTER = 64;
const MARGIN_GUTTER = 16;

type ExportPDFType = Readonly<{
	fileName: string;
	elementId: string;
	orientation: 'p' | 'l' | 'portrait' | 'landscape';
}>;

const pdfAtom = atom<ExportPDFType | null>(null);

type ReturnType = Readonly<{
	setPdfExportData: (data: ExportPDFType | null) => void;
	generatePdf: () => Promise<void>;
	isExporting: boolean;
}>;

/**
 * Generic hook to export page elements into PDF
 * Firstly use 'setPdfExportData' to set all the necessary meta data for the pdf export
 * ...
 * Then use the 'generatePDF' method to generate the PDF
 * In order to export pages containing other scrollable elements, add data attribute 'data-apply-visible-overflow'
 * This method takes care of setting app into light mode and applying the overflow class when data attribute is present.
 * It generates single page PDF containng the desired element
 * When viewport is smaller than 992px, the main exported element receives a class that ensures minimal width of 1920px
 * In order to export page that has breakpoints based layout, there is `isExporting` state available, so that the style change
 * can occur only when the pdf export is happening.
 * @returns {ReturnType}
 */
export const useGeneratePDF = (): ReturnType => {
	const [pdfExportData, setPdfExportData] = useAtom(pdfAtom);
	const [imageUrl, setImageUrl] = useState<string | null>(null);
	const { openSnackbar } = useSnackbar();
	const logo = useMemo(() => new Image(), []);
	logo.src = '/logo-light.svg';
	const intl = useIntl();
	const { oodMode, setOodMode } = useThemeMode();
	const [isExporting, setIsExporting] = useState(false);

	useEffect(() => {
		logo.onload = () => {
			const canvas = document.createElement('canvas');
			canvas.width = logo.width;
			canvas.height = logo.height;

			const context = canvas.getContext('2d');

			context?.drawImage(logo, 0, 0);

			const dataURL = canvas.toDataURL('image/png');
			setImageUrl(dataURL);
		};
	}, [logo]);

	const generatePdf = useCallback(async (): Promise<void> => {
		openSnackbar({
			open: true,
			variant: 'alert',
			alert: {
				color: 'info',
			},
			close: true,
			message: intl.formatMessage({ id: 'export.started' }),
		});

		const mode = oodMode;
		setTimeout(async () => {
			if (pdfExportData == null) {
				openSnackbar({
					open: true,
					variant: 'alert',
					alert: {
						color: 'error',
					},
					close: true,
					message: intl.formatMessage({ id: 'export.missing-export-data' }),
				});
				return;
			}
			const element = document.getElementById(pdfExportData.elementId) as HTMLDivElement | null;
			if (element?.hasAttribute('data-apply-visible-overflow')) {
				element.classList.add('show-overflow');
			}

			if (window.innerWidth < 992 && element) {
				element.classList.add('min-pdf-export-width');
			}

			if (element == null) {
				openSnackbar({
					open: true,
					variant: 'alert',
					alert: {
						color: 'error',
					},
					close: true,
					message: intl.formatMessage({ id: 'export.no-element-dimensions' }),
				});
				return;
			}

			const width = element.scrollWidth + GUTTER;
			const height = element.scrollHeight + GUTTER * 2;

			const pdfContent = await dom2image.toJpeg(element, {
				width: element.scrollWidth - 16,
				quality: 1,
				bgcolor: '#FFF',
				filter: (node) => {
					// 1 = HtmlElement
					if (node.nodeType === 1) {
						return !(node as HTMLElement).hasAttribute('data-ignore-pdf');
					}
					return true;
				},
			});

			const pdf = new jsPDF({
				unit: 'px',
				orientation: pdfExportData.orientation,
				format: [width, height],
			});

			pdf.setPage(1);
			pdf.addImage(pdfContent, MARGIN_GUTTER * 2, GUTTER, element.scrollWidth, element.scrollHeight);
			// This part adds watermark to the PDF
			pdf.setTextColor(150);
			pdf.text(intl.formatMessage({ id: 'common.created-by' }), MARGIN_GUTTER, MARGIN_GUTTER + 8);
			pdf.addImage(imageUrl ?? '', 'PNG', MARGIN_GUTTER, 32, 97, 10);

			openSnackbar({
				open: true,
				variant: 'alert',
				alert: {
					color: 'success',
				},
				close: true,
				message: intl.formatMessage({ id: 'export.successful' }),
			});

			const pageCount = pdf.getNumberOfPages();

			if (pageCount > 1) {
				pdf.deletePage(pageCount);
			}

			pdf.save(pdfExportData.fileName || 'ood_export');
			element.classList.remove('show-overflow');
			element.classList.remove('min-pdf-export-width');
			setOodMode(mode);
			setIsExporting(false);
		}, 750);
		setOodMode(OodThemeMode.LIGHT);
		setIsExporting(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pdfExportData, imageUrl, oodMode, isExporting]);

	return { setPdfExportData, generatePdf, isExporting };
};
